import { useEffect, useState, lazy, Suspense } from "react";
import axios from "axios";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import LandingPage from "@/pages/LandingPage";
import Login from "@/pages/LoginNew";
import { ImpersonationProvider } from "@/context/ImpersonationContext";
import { BrandingProvider } from "@/context/BrandingContext";
import PoweredByFooter from "@/components/common/PoweredByFooter";
import MiraCopilot from "@/components/common/MiraCopilot";
import BrandSuite from "@/components/common/BrandSuite";
import { Toaster } from "@/components/ui/sonner";
import { installAxiosErrorNormaliser } from "@/utils/axiosErrorNormaliser";

// Coerce FastAPI validation-error arrays into a plain string before any
// component ever reads `err.response.data.detail`. Prevents the classic
// "Objects are not valid as a React child" crash when toast.error() is
// handed a Pydantic error array. Must run before any dashboard mounts.
installAxiosErrorNormaliser();

// ─── Route-level code splitting ──────────────────────────────────────────────
// Each dashboard is only downloaded when a user with that role first navigates
// to it. Cuts the initial JS payload from ~940 kB gzip → ~250 kB gzip.
// Keep Login + LandingPage eager (small pages, always the first hop).
const SuperAdminDashboard  = lazy(() => import("@/pages/SuperAdminDashboard"));
const SchoolAdminDashboard = lazy(() => import("@/pages/SchoolAdminDashboard"));
const ChainAdminDashboard  = lazy(() => import("@/pages/ChainAdminDashboard"));
const TeacherDashboard     = lazy(() => import("@/pages/TeacherDashboard"));
const StudentDashboard     = lazy(() => import("@/pages/StudentDashboard"));
const ParentDashboard      = lazy(() => import("@/pages/ParentDashboard"));
const StaffDashboard       = lazy(() => import("@/pages/StaffDashboard"));
const DriverDashboard      = lazy(() => import("@/pages/DriverDashboard"));
const TrainingManagerDashboard = lazy(() => import("@/pages/TrainingManagerDashboard"));
const TrainerPortal        = lazy(() => import("@/pages/TrainerPortal"));
const ResetPassword        = lazy(() => import("@/pages/ResetPassword"));
const PrivacyPolicy        = lazy(() => import("@/pages/PrivacyPolicy"));
const TakeAssessment       = lazy(() => import("@/components/student/TakeAssessment"));
const ViewResult           = lazy(() => import("@/components/student/ViewResult"));
const AssessmentDetails    = lazy(() => import("@/components/teacher/AssessmentDetails"));
const SchoolsOverview      = lazy(() => import("@/components/superadmin/SchoolsOverview"));
const MarketplaceLanding   = lazy(() => import("@/pages/marketplace/MarketplaceLanding"));

const RouteFallback = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="flex items-center gap-3 text-gray-600">
      <div className="w-8 h-8 border-2 border-purple-600 border-t-transparent rounded-full animate-spin" />
      <span className="text-sm">Loading…</span>
    </div>
  </div>
);

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem("token");
    const storedUser = localStorage.getItem("user");
    if (token && storedUser) {
      setUser(JSON.parse(storedUser));
    }
    setLoading(false);
  }, []);

  // Notify mounted dashboards after any successful write. This keeps metrics
  // current when an admin creates a payment, student, attendance record, fee,
  // enquiry, staff member, room, or class in a child module.
  useEffect(() => {
    const interceptor = axios.interceptors.response.use((response) => {
      const method = String(response.config?.method || "").toLowerCase();
      if (["post", "put", "patch", "delete"].includes(method)) {
        window.dispatchEvent(new CustomEvent("lmsDataChanged", { detail: { url: response.config?.url, method } }));
      }
      return response;
    });
    return () => axios.interceptors.response.eject(interceptor);
  }, []);

  const ProtectedRoute = ({ children, allowedRoles }) => {
    if (loading) return <RouteFallback />;
    if (!user) return <Navigate to="/login" />;
    if (allowedRoles && !allowedRoles.includes(user.role)) {
      return <Navigate to="/login" />;
    }
    return children;
  };

  const handleMiraNavigate = (moduleId) => {
    const role = user?.role;
    const dashboard = ["teacher"].includes(role)
      ? "/teacher"
      : ["student"].includes(role)
      ? "/student"
      : ["chain_admin"].includes(role)
      ? "/chain-admin"
      : "/school-admin";
    const basePath = (process.env.PUBLIC_URL || "").replace(/\/$/, "");
    window.location.href = `${basePath}${dashboard}#${encodeURIComponent(moduleId || "overview")}`;
  };

  return (
    <div className="App">
      <header className="sticky top-0 z-40 border-b border-slate-200 bg-white/95 shadow-sm backdrop-blur-sm">
        <BrandSuite className="mx-auto h-14 max-w-7xl px-4 sm:h-16 sm:px-6 lg:px-8" />
      </header>
      <BrandingProvider>
        <BrowserRouter basename={process.env.PUBLIC_URL || "/"}>
          <ImpersonationProvider>
            <Suspense fallback={<RouteFallback />}>
              <Routes>
                <Route path="/" element={<LandingPage />} />
                <Route path="/login" element={<Login setUser={setUser} />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                <Route path="/marketplace/*" element={<MarketplaceLanding />} />
                <Route
                  path="/super-admin/*"
                  element={
                    <ProtectedRoute allowedRoles={["super_admin"]}>
                      <SuperAdminDashboard user={user} setUser={setUser} />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/superadmin/schools-overview"
                  element={
                    <ProtectedRoute allowedRoles={["super_admin"]}>
                      <SchoolsOverview />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/chain-admin/*"
                  element={
                    <ProtectedRoute allowedRoles={["chain_admin"]}>
                      <ChainAdminDashboard user={user} setUser={setUser} />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/school-admin/*"
                  element={
                    <ProtectedRoute allowedRoles={["institute_admin", "super_admin", "chain_admin"]}>
                      <SchoolAdminDashboard user={user} setUser={setUser} />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/teacher/*"
                  element={
                    <ProtectedRoute allowedRoles={["teacher"]}>
                      <TeacherDashboard user={user} setUser={setUser} />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/teacher/assessment-details/:assessmentId"
                  element={
                    <ProtectedRoute allowedRoles={["teacher"]}>
                      <AssessmentDetails />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/student/*"
                  element={
                    <ProtectedRoute allowedRoles={["student"]}>
                      <StudentDashboard user={user} setUser={setUser} />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/take-assessment/:assessmentId"
                  element={
                    <ProtectedRoute allowedRoles={["student"]}>
                      <TakeAssessment />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/view-result/:submissionId"
                  element={
                    <ProtectedRoute allowedRoles={["student"]}>
                      <ViewResult />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/parent/*"
                  element={
                    <ProtectedRoute allowedRoles={["parent"]}>
                      <ParentDashboard user={user} setUser={setUser} />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/staff/*"
                  element={
                    <ProtectedRoute allowedRoles={["staff"]}>
                      <StaffDashboard user={user} setUser={setUser} />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/driver/*"
                  element={
                    <ProtectedRoute allowedRoles={["driver"]}>
                      <DriverDashboard user={user} setUser={setUser} />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/training-manager/*"
                  element={
                    <ProtectedRoute allowedRoles={["training_manager"]}>
                      <TrainingManagerDashboard user={user} setUser={setUser} />
                    </ProtectedRoute>
                  }
                />
                <Route path="/trainer/*" element={<TrainerPortal />} />
                <Route path="/" element={<Navigate to="/login" />} />
              </Routes>
            </Suspense>
            <Toaster position="top-right" />
            <PoweredByFooter />
            {user && <MiraCopilot user={user} onNavigate={handleMiraNavigate} />}
          </ImpersonationProvider>
        </BrowserRouter>
      </BrandingProvider>
    </div>
  );
}

export default App;
